// Chip-based tag editor — matches prototype TaskModal layout; edits via popovers.
import { useMemo, useRef, useState } from 'react';
import { TaskFormTagChips } from '@/features/tasks/components/forms/fields/TaskFormTagChips';
import { TaskFormTagMenus } from '@/features/tasks/components/forms/fields/TaskFormTagMenus';
import { useT } from '@/i18n/useT';
import {
  buildAddMenuItems,
  commitInlineTagAdd,
  getInlineTagOptions,
  getInlineTagPlaceholder,
} from '@/features/tasks/components/forms/fields/taskFormTagSectionHelpers';

/** @typedef {'project'|'context'|'person'|'parent'} TagEditorKind */
/** @typedef {'project'|'context'|'person'} InlineAddKind */

/**
 * @param {Object} props
 * @param {import('@/types').Task} props.task
 * @param {string[]} props.projectOptions
 * @param {string[]} props.contextOptions
 * @param {string[]} props.personOptions
 * @param {Array<{id: string|null, title: string}>} props.parentTaskOptions
 * @param {Map<string, import('@/types').Task>} props.tasksMap
 * @param {import('@/features/tasks/context/TasksContext.def').TasksContextValue['projectDisplayMap']} props.projectDisplayMap
 * @param {import('@/features/tasks/context/TasksContext.def').TasksContextValue['contextDisplayMap']} props.contextDisplayMap
 * @param {(event: unknown, value: string[]) => void} props.handleProjectsChange
 * @param {(event: unknown, value: string[]) => void} props.handleContextsChange
 * @param {(event: unknown, value: string|null) => void} props.handlePersonChange
 * @param {(event: unknown, value: {id: string|null, title: string}|null) => void} props.onParentChange
 */
const TaskFormTagSection = ({
  task,
  projectOptions,
  contextOptions,
  personOptions,
  parentTaskOptions,
  tasksMap,
  projectDisplayMap,
  contextDisplayMap,
  handleProjectsChange,
  handleContextsChange,
  handlePersonChange,
  onParentChange,
}) => {
  const t = useT();
  const addTagChipRef = useRef(/** @type {HTMLDivElement|null} */ (null));
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [editorAnchor, setEditorAnchor] = useState(null);
  const [editorKind, setEditorKind] = useState(
    /** @type {TagEditorKind|null} */ (null),
  );
  const [inlineAddKind, setInlineAddKind] = useState(
    /** @type {InlineAddKind|null} */ (null),
  );
  const [inlineDraft, setInlineDraft] = useState('');

  const parentLabel = useMemo(() => {
    if (!task?.parentId) return t('taskform.rootParent', 'Hoofdniveau');
    const parent = tasksMap.get(task.parentId);
    return parent?.title ?? t('taskform.rootParent', 'Hoofdniveau');
  }, [task?.parentId, tasksMap, t]);

  const hasProject = (task.projects || []).length > 0;
  const hasContext = (task.contexts || []).length > 0;
  const addMenuItems = useMemo(
    () => buildAddMenuItems(hasProject, hasContext, t),
    [hasProject, hasContext, t],
  );

  const openEditor = (kind) => {
    setAddMenuAnchor(null);
    setInlineAddKind(null);
    setInlineDraft('');
    setEditorAnchor(addTagChipRef.current);
    setEditorKind(kind);
  };

  const startInlineAdd = (kind) => {
    setAddMenuAnchor(null);
    closeEditor();
    setInlineAddKind(kind);
    setInlineDraft('');
  };

  const cancelInlineAdd = () => {
    setInlineAddKind(null);
    setInlineDraft('');
  };

  const commitInlineAdd = (rawValue) => {
    commitInlineTagAdd({
      inlineAddKind,
      inlineDraft,
      rawValue,
      handleProjectsChange,
      handleContextsChange,
      handlePersonChange,
      cancelInlineAdd,
    });
  };

  const closeEditor = () => {
    setEditorAnchor(null);
    setEditorKind(null);
  };

  const removeProject = (project) => {
    handleProjectsChange(
      null,
      (task.projects || []).filter((p) => p !== project),
    );
  };

  const removeContext = (context) => {
    handleContextsChange(
      null,
      (task.contexts || []).filter((c) => c !== context),
    );
  };

  const inlineOptions = getInlineTagOptions(
    inlineAddKind,
    projectOptions,
    contextOptions,
    personOptions,
  );
  const inlinePlaceholder = getInlineTagPlaceholder(inlineAddKind, t);

  const handleAddMenuSelect = (kind) => {
    if (kind === 'parent') openEditor('parent');
    else startInlineAdd(kind);
  };

  return (
    <>
      <TaskFormTagChips
        task={task}
        projectDisplayMap={projectDisplayMap}
        contextDisplayMap={contextDisplayMap}
        removeProject={removeProject}
        removeContext={removeContext}
        handlePersonChange={handlePersonChange}
        parentLabel={parentLabel}
        onOpenParentEditor={() => openEditor('parent')}
        inlineAddKind={inlineAddKind}
        inlineDraft={inlineDraft}
        setInlineDraft={setInlineDraft}
        inlineOptions={inlineOptions}
        inlinePlaceholder={inlinePlaceholder}
        commitInlineAdd={commitInlineAdd}
        cancelInlineAdd={cancelInlineAdd}
        addTagChipRef={addTagChipRef}
        onOpenAddMenu={(event) => setAddMenuAnchor(event.currentTarget)}
      />

      <TaskFormTagMenus
        addMenuAnchor={addMenuAnchor}
        onCloseAddMenu={() => setAddMenuAnchor(null)}
        addMenuItems={addMenuItems}
        onAddMenuSelect={handleAddMenuSelect}
        editorAnchor={editorAnchor}
        onCloseEditor={closeEditor}
        editorKind={editorKind}
        task={task}
        parentTaskOptions={parentTaskOptions}
        tasksMap={tasksMap}
        onParentChange={onParentChange}
      />
    </>
  );
};

export default TaskFormTagSection;
